import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, Crown, Check, Package, RefreshCw, ChevronDown, Rocket } from "lucide-react";

// O registro mora em @/lib/plans; re-exportado aqui porque Subscribe, Dashboard
// e Settings importam deste módulo desde antes do registro existir.
export {
  PLAN_DEFS,
  PLAN_RANK,
  isPaidPlanId,
  isUltra,
  planAllowsPrinterUI,
  priceCents,
  totalCents,
  formatBRL,
  type PaidPlanId,
  type BillingCycleUI,
} from "@/lib/plans";
import { PLAN_DEFS, priceCents, totalCents, formatBRL, type PaidPlanId, type BillingCycleUI } from "@/lib/plans";

export function PlanPicker({
  used,
  subscribing,
  onSubscribe,
  showProducts,
  showServices,
  showStatusCard = true,
  showUpgradeCard = true,
  onUpgradeClick,
  freeOrderLimit = 30,
  nextResetDate,
}: {
  used: number;
  subscribing: boolean;
  onSubscribe: (plan: PaidPlanId, cycle: BillingCycleUI) => void;
  showProducts: boolean;
  showServices: boolean;
  printerConnected?: boolean;
  showStatusCard?: boolean;
  showUpgradeCard?: boolean;
  onUpgradeClick?: () => void;
  freeOrderLimit?: number;
  nextResetDate?: Date | null;
}) {
  const [cycle, setCycle] = useState<BillingCycleUI>("monthly");
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.location.hash === "#assinar") return true;
    return used >= 5;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => { if (window.location.hash === "#assinar") setOpen(true); };
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  const limit = freeOrderLimit;
  const remaining = Math.max(0, limit - used);
  const pct = Math.min(100, (used / limit) * 100);
  const now = new Date();
  const nextReset = nextResetDate ?? new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const isNearLimit = used >= 25;
  const isAtLimit = used >= limit;
  const orderLabel = showProducts && showServices ? "Pedidos e agendamentos ilimitados" : showServices ? "Agendamentos ilimitados" : "Pedidos ilimitados";

  const def = PLAN_DEFS.profissional;
  const cents = priceCents("profissional", cycle);
  const featList = [orderLabel, ...def.features.filter((f) => f !== "Pedidos ilimitados")];
  // Ultra: o card mostra o resumo de 4 linhas do registro; "e mais N" fecha com
  // a lista inteira que ele vai encontrar na página /assinar.
  const ultraDef = PLAN_DEFS.ultra;
  const ultraCents = priceCents("ultra", cycle);
  const ultraFeats = ultraDef.highlights ?? ultraDef.features.slice(0, 4);
  const ultraRest = ultraDef.features.length - ultraFeats.length;
  // 12 mensais − o anual, em reais: R$100 no Profissional, R$394 no Ultra.
  const descontoAnual = (p: PaidPlanId) =>
    Math.round((PLAN_DEFS[p].monthlyCents * 12 - PLAN_DEFS[p].yearlyCents) / 100);

  const statusCardNode = showStatusCard && used > 24 ? (
    <Card className={`p-6 border ${isAtLimit ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20' : isNearLimit ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20' : 'border-border'}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Pedidos do mês (Plano Grátis)</p>
          <h3 className="text-3xl font-bold mt-1">{remaining} <span className="text-sm font-normal text-muted-foreground">restantes de {limit}</span></h3>
        </div>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isAtLimit ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : isNearLimit ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' : 'bg-primary/10 text-primary'}`}>
          <Package className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
          <RefreshCw className="w-3 h-3" />
          <span>Renova em {nextReset.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}</span>
        </div>
        <div className="w-full bg-muted rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all ${isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-primary'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      {!showUpgradeCard && onUpgradeClick && (
        <Button
          size="sm"
          className="w-full mt-4 cursor-pointer"
          onClick={onUpgradeClick}
        >
          <Crown className="w-4 h-4 mr-2" />
          Assinar Ilimitado
        </Button>
      )}
    </Card>
  ) : null;

  return (
    <div className="space-y-4">
      {!showUpgradeCard && statusCardNode}

      {showUpgradeCard && (
      <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="p-4 border-2 border-accent/30 bg-gradient-to-br from-accent/5 to-accent/15">
        <CollapsibleTrigger className="group flex w-full items-center justify-between cursor-pointer">
          <div className="flex items-center gap-2 text-base font-semibold">
            <Crown className="w-5 h-5 text-primary" /> Assinatura
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
        <div id="assinar" className="scroll-mt-20" aria-hidden="true" />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Crown className="w-5 h-5 text-primary" /> Faça um upgrade pra liberar mais
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="inline-flex bg-muted rounded-full p-1 text-xs font-medium">
            <button
              type="button"
              onClick={() => setCycle("monthly")}
              className={`px-3 py-1 rounded-full transition-colors cursor-pointer ${cycle === "monthly" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
            >
              Mensal
            </button>
            <button
              type="button"
              onClick={() => setCycle("yearly")}
              className={`px-3 py-1 rounded-full transition-colors cursor-pointer ${cycle === "yearly" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
            >
              Anual
            </button>
            </div>
          </div>
        </div>

        <div className="rounded-xl p-4 flex flex-col bg-card border-2 border-accent shadow-md">
          <div className="flex items-baseline justify-between">
            <h4 className="font-bold text-lg">{def.name}</h4>
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-2xl font-bold text-primary">{formatBRL(cents)}</span>
            <span className="text-xs text-muted-foreground">/{cycle === "yearly" ? "ano" : "mês"}</span>
          </div>
          {cycle === "yearly" && (
            <p className="text-[11px] font-bold text-accent mt-1">R${descontoAnual("profissional")} desconto!</p>
          )}
          <ul className="mt-3 space-y-1.5 flex-1">
            {featList.map((f) => (
              <li key={f} className="text-xs flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            className="w-full mt-3 cursor-pointer"
            disabled={subscribing}
            onClick={() => onSubscribe("profissional", cycle)}
          >
            {subscribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
            Assinar {def.name}
          </Button>
        </div>

        <div className="rounded-xl p-4 flex flex-col bg-card border-2 border-accent/40">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-bold text-lg flex items-center gap-1.5">
              <Rocket className="w-4 h-4 text-primary" /> {ultraDef.name}
            </h4>
            <span className="rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 whitespace-nowrap">
              TUDO LIBERADO
            </span>
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-2xl font-bold text-primary">{formatBRL(ultraCents)}</span>
            <span className="text-xs text-muted-foreground">/{cycle === "yearly" ? "ano" : "mês"}</span>
          </div>
          {cycle === "yearly" && (
            <p className="text-[11px] font-bold text-accent mt-1">R${descontoAnual("ultra")} desconto!</p>
          )}
          <ul className="mt-3 space-y-1.5 flex-1">
            {ultraFeats.map((f) => (
              <li key={f} className="text-xs flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {ultraRest > 0 && (
            <p className="text-[11px] text-muted-foreground mt-1.5 pl-5">e mais {ultraRest} recursos</p>
          )}
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-3 cursor-pointer border-primary text-primary hover:bg-primary/10"
            disabled={subscribing}
            onClick={() => onSubscribe("ultra", cycle)}
          >
            {subscribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
            Assinar {ultraDef.name}
          </Button>
        </div>
        {statusCardNode}
        </CollapsibleContent>
      </Card>
      </Collapsible>
      )}
    </div>
  );
}
